import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useTenantStore } from '@/store/tenantStore';
import { ROUTES } from '@/app/router/routes';
import { analyticsService } from '@/services/analytics/analyticsService';
import type { AggregationValue, SearchInput } from '@/types/job.types';
import { EmptyState, SkeletonBar } from '@/components/feedback/ResultStates';
import styles from './JobCategorySection.module.css';

/**
 * JobCategorySection — "Jobs by category" with tabs + tile grid.
 * Mirrors Flutter WebCareerPage: job category tabs + JobsByCategoryContainer.
 */

interface JobCategorySectionProps {
  /** Home's current search input — a tile keeps it (e.g. isHotJob) and replaces only the filters */
  searchInput: SearchInput;
  categories: AggregationValue[];
  selectedIndex: number;
  showViewAll: boolean;
  isLoading: boolean;
  onSelectTab: (index: number) => void;
  onToggleViewAll: (index: number) => void;
}

const SHIMMER_COUNT = 6;
// Enough tiles to fill the empty state's two rows at any width (the grid clips the rest)
const GHOST_COUNT = 20;

/** A category tile's outline: name over the jobs-available count. */
function TileSkeleton() {
  return (
    <div className={styles.skeletonTile} aria-hidden="true">
      <SkeletonBar width="64%" height={14} />
      <SkeletonBar width="42%" />
    </div>
  );
}

export function JobCategorySection({
  searchInput,
  categories,
  selectedIndex,
  showViewAll,
  isLoading,
  onSelectTab,
  onToggleViewAll,
}: JobCategorySectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getJobCategories = useTenantStore((s) => s.getJobCategories);
  const jobCategoryTabs = getJobCategories();

  const isMobile = useIsMobile();

  const visibleLimit = isMobile ? 3 : 6;
  const displayedCategories = showViewAll
    ? categories
    : categories.slice(0, visibleLimit);
  const hasOverflow = categories.length > visibleLimit;

  // Flutter WebHomeBloc._OnClickCategoryCard: state.searchInput with filters + categoryDetails replaced
  const handleCategoryClick = (value: AggregationValue) => {
    if (!value.count) return;
    void analyticsService.logEvent('home_page_category_click', value.displayKey ?? '');
    const catTab = jobCategoryTabs[selectedIndex];
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS });
    navigate(ROUTES.JOBS, {
      state: {
        searchInput: {
          ...searchInput,
          filters: {
            [catTab.key]: [
              { key: value.key, displayKey: value.displayKey, isChecked: true },
            ],
          },
          categoryDetails: {
            key: catTab.key,
            value: value.key,
            displayKey: catTab.title,
            displayValue: value.displayKey,
          },
        },
      },
    });
  };

  return (
    <section className={styles.section}>
      {/* Section heading */}
      <div className={styles.header}>
        <span className={styles.accent} />
        <h2 className={styles.title}>{t('Jobs by category')}</h2>
        <div className={styles.spacer} />

        {/* Category tabs */}
        <div className={styles.tabs}>
          {jobCategoryTabs.map((tab, idx) => (
            <button
              key={tab.key}
              className={`${styles.tabBtn} ${idx === selectedIndex ? styles.tabActive : ''}`}
              onClick={() => onSelectTab(idx)}
            >
              {t(tab.title)}
            </button>
          ))}
        </div>
      </div>

      {/* Category tiles */}
      {isLoading ? (
        <div className={styles.grid}>
          {Array.from({ length: SHIMMER_COUNT }).map((_, i) => (
            <TileSkeleton key={i} />
          ))}
        </div>
      ) : categories.length === 0 ? (
        // Flutter JobsByCategoryContainer: GenericWidget.noRecords() when categData is empty
        <EmptyState
          message={t('No Records')}
          ghost={
            <div className={`${styles.grid} ${styles.ghostGrid}`}>
              {Array.from({ length: GHOST_COUNT }).map((_, i) => (
                <TileSkeleton key={i} />
              ))}
            </div>
          }
        />
      ) : (
        <>
          <div className={styles.grid}>
            {displayedCategories.map((cat) => (
              <button
                key={cat.key}
                className={styles.tile}
                disabled={!cat.count}
                onClick={() => handleCategoryClick(cat)}
              >
                <div className={styles.tileText}>
                  <div className={styles.tileName}>
                    {cat.key === 'Lateral' ? t('Experienced') : (cat.displayKey ?? cat.key)}
                  </div>
                  <div className={styles.tileCount}>
                    {cat.count
                      ? `${cat.count} ${cat.count !== 1 ? t('jobs available') : t('job available')}`
                      : t('No jobs available')}
                  </div>
                </div>
                {!!cat.count && (
                  <span className={styles.tileArrow} aria-hidden="true">→</span>
                )}
              </button>
            ))}
          </div>

          {/* View all / View less toggle */}
          {hasOverflow && (
            <button
              className={styles.viewToggle}
              onClick={() => onToggleViewAll(selectedIndex)}
            >
              {showViewAll ? t('View less') : t('View all')}
              <span className={styles.viewArrow}>›</span>
            </button>
          )}
        </>
      )}
    </section>
  );
}
